import { HttpClient, HttpErrorResponse, HttpEvent, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {Injectable} from '@angular/core';

import {
  ACCESS_TOKEN_KEY,
  APP_VERSION,
  AUTH_SCHEME,
  CURRENT_USER,
  EMAIL_TO_SEND,
  REFRESH_TOKEN_KEY,
  SIGNIN_USER
} from '../shared/constants';
import {environment} from '../../environments/environment';
import { API_URL } from './endpoints';
import { AppNotify, AppStorage } from '../utilities';
import { LoggedUserService } from './logged-user.service';
import { ResponseModel } from '../models/shared';
import { SentryService } from './sentry.service';

@Injectable({ providedIn: 'root' })
export class ApiService {

    constructor(private httpClient: HttpClient,
              private loggedUserService: LoggedUserService,
              private sentryService: SentryService) {
  }

  get accessToken(): string {
    return AppStorage.getTokenData(ACCESS_TOKEN_KEY);
  }

  get refreshToken(): string {
    return AppStorage.getTokenData(REFRESH_TOKEN_KEY);
  }

  get currentUser(): string {
    return AppStorage.getTokenData(CURRENT_USER);
  }

  get emailToSend(): string {
    return AppStorage.getTokenData(EMAIL_TO_SEND);
  }

  get signInUser(): string {
    return AppStorage.getTokenData(SIGNIN_USER);
  }

  get headerAuthorizationKey(): string {
    return AUTH_SCHEME + this.accessToken;
  }

  get apiHost(): string {
    return API_URL;
  }

  get headers(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      APIKey: '~123456789~',
      AppVersion: environment.version,
      Authorization: this.headerAuthorizationKey,
    });
  }

  get pdfHeaders(): HttpHeaders {
    const header = this.headers;
    header.delete('Content-Type');
    header.set('Content-Type', 'application/pdf');

    return header;
  }

  get noAuthHeader(): HttpHeaders {
    const header = this.headers;
    header.delete('Authorization');

    return header;
  }

  get options() {
    return {headers: this.headers};
  }

  get noAuthOptions() {
    return {headers: this.noAuthHeader};
  }

  get formDataHeader(): HttpHeaders {
    const header = this.headers;
    header.delete('Content-Type');

    return header;
  }

  get formDataHeaders(): HttpHeaders {
    // skipcq: JS-0323
    return new HttpHeaders(this.formDataHeader as any);
  }
  
  private _makeHttpOptions = (headers: HttpHeaders) => ({
    headers,
    observe: 'response' as const
  });

  get<T>(url: string): Observable<ResponseModel<T>> {
    return this.httpClient
      .get<ResponseModel<T>>(`${API_URL}/${url}`, this._makeHttpOptions(this.options.headers))
      .pipe(
        tap((response: HttpResponse<ResponseModel<T>>) => this.handleResponse(response))
      , map((response: HttpResponse<ResponseModel<T>>) => response.body as ResponseModel<T>));
  }

  getArraybuffer<T>(url: string): Observable<ArrayBuffer> {
    return this.httpClient.get(`${API_URL}/${url}`, {responseType: 'arraybuffer', headers: this.pdfHeaders});
  }

  getBlob(url: string): Observable<Blob> {
    return this.httpClient.get(`${API_URL}/${url}`, {responseType: 'blob', headers: this.headers});
  }

  getText(url: string): Observable<string> {
    return this.httpClient.get(url, {responseType: 'text', headers: this.headers});
  }

  postBlob<T>(url: string, data: T): Observable<Blob> {
    return this.httpClient.post(`${API_URL}/${url}`, data, {responseType: 'blob', headers: this.headers});
  }

  post<T>(url: string, data: unknown, noAuth: boolean = false): Observable<ResponseModel<T>> {
    const request$ = data instanceof FormData
      ? this.httpClient.post<ResponseModel<T>>(`${API_URL}/${url}`, data, this._makeHttpOptions(this.formDataHeaders))
      : this.httpClient.post<ResponseModel<T>>(`${API_URL}/${url}`, data, this._makeHttpOptions(noAuth ? this.noAuthOptions.headers : this.options.headers));

    return request$.pipe(
      tap((response: HttpResponse<ResponseModel<T>>) => {
        this.handleResponse(response);
      }),
      map(response => response.body as ResponseModel<T>) // Return body only
    );
  }

  postFormData<T>(url: string, formData: FormData): Observable<ResponseModel<T>> {
    return this.httpClient
    .post<ResponseModel<T>>(`${API_URL}/${url}`, formData)
    .pipe(tap(res => {
      if (res.isError) {
        this.handleResponseData(res);
      }
    }));
  }

  postFileData<T>(url: string, files: File[]): Observable<ResponseModel<T>> {
    const formData: FormData = new FormData();
    for (const file of files) {
      formData.append(file.name, file, file.name);
    }
    const httpOptions = {
      headers: this.formDataHeaders
    };
    return this.httpClient
    .post<ResponseModel<T>>(`${API_URL}/${url}`, formData, httpOptions)
    .pipe(tap(res => {
      if (res.isError) {
        this.handleResponseData(res);
      }
    }));
  }

  postFile<T>(url: string, files: File[]): Observable<HttpEvent<T>> {
    const formData: FormData = new FormData();
    for (const file of files) {
      formData.append(file.name, file, file.name);
    }
    const headers = this.formDataHeaders;
    const uploadReq = new HttpRequest('POST', `${API_URL}/${url}`, formData, {
      reportProgress: true,
      headers
    });

    return this.httpClient.request<T>(uploadReq);
  }

  postFileIncludeData<T>(url: string, files: File[], data: unknown): Observable<HttpEvent<ResponseModel<T>>> {
    const formData: FormData = new FormData();
    for (const file of files) {
      formData.append(file.name, file, file.name);
    }
    formData.append('data', JSON.stringify(data));
    const headers = this.formDataHeaders;
    const uploadReq = new HttpRequest('POST', `${API_URL}/${url}`, formData, {
      reportProgress: true,
      headers
    });

    return this.httpClient.request<ResponseModel<T>>(uploadReq);
  }

  update<T>(url: string, data?: unknown): Observable<ResponseModel<T>> {
    if (data instanceof FormData) {
      const httpOptions = {
        headers: this.formDataHeaders
      };
      return this.httpClient.put<ResponseModel<T>>(`${API_URL}/${url}`, data, httpOptions)
        .pipe(tap(res => {
          if (res.isError) {
            this.handleResponseData(res);
          }
        }));
    } else {
      return this.httpClient
        .put<ResponseModel<T>>(`${API_URL}/${url}`, data, this.options)
        .pipe(tap(res => {
          if (res.isError) {
            this.handleResponseData(res);
          }
        }));
    }
  }

  patch<T>(url: string, data?: unknown): Observable<ResponseModel<T>> {
    return this.httpClient
      .patch<ResponseModel<T>>(`${API_URL}/${url}`, data, this.options)
      .pipe(tap(res => {
        if (res.isError) {
          this.handleResponseData(res);
        }
      }));
  }

  delete<T>(url: string): Observable<ResponseModel<T>> {
    const params: URLSearchParams = new URLSearchParams();
    const options: { headers: HttpHeaders; search?: URLSearchParams } = { headers: this.headers };

    if (params) {
      for (const key in params) {
        if (params.hasOwnProperty(key)) {
          const val = params.get(key);
          if (val)
            params.set(key, val);
        }
      }

      options.search = params;
    }

    return this.httpClient
      .delete<ResponseModel<T>>(`${API_URL}/${url}`, options)
      .pipe(tap(res => {
        if (res.isError) {
          this.handleResponseData(res);
        }
      }));
  }

  private handleResponse<T>(res: HttpResponse<ResponseModel<T>>) {
    const contentType = res.headers.get('X-User-Locale');
    
    AppStorage.storeLocalStorageDataRaw('X-User-Locale', contentType ?? '');
    const userCurrency = res.headers.get('X-User-Currency');
    if (userCurrency) {
      AppStorage.storeLocalStorageDataRaw('X-User-Currency', userCurrency);
    }

    if (res.body?.isError) {
      if (res.status === 402 || res.body.status === 402) {
        // ignore capture error in sentry
        return;
      }
      const errorMessage = res.body?.message ? res.body?.message : 'Internal Server Error';
      
      // Capture API response errors in Sentry
      this.sentryService.captureApiError(
        new Error(errorMessage),
        res.url || 'unknown',
        { 
          component: 'ApiService', 
          method: 'handleResponse',
          additionalData: { 
            responseBody: res.body,
            statusCode: res.status
          }
        }
      );
      
      AppNotify.error(errorMessage, 5000);
    }
  }

  private handleResponseData<T>(data: ResponseModel<T>) {
    if (data.isError) {
      const errorMessage = data.message ? data.message : 'Internal Server Error';
      
      // Capture API data errors in Sentry
      this.sentryService.captureApiError(
        new Error(errorMessage),
        'unknown',
        { 
          component: 'ApiService', 
          method: 'handleResponseData',
          additionalData: { responseData: data }
        }
      );
      
      AppNotify.error(errorMessage, 5000);
    }
  }



  changeUser(value: string, emailToSend: string) {
    AppStorage.storeTokenData(CURRENT_USER, value);
    AppStorage.storeTokenData(EMAIL_TO_SEND, emailToSend);
  }

  saveAppVersion(value: string) {
    AppStorage.storeLocalStorageData(APP_VERSION, value);
  }

  getAppVersion() {
    return AppStorage.getLocalStorageData(APP_VERSION);
  }
}
